





















































































































































































import { Component, Prop, Vue } from 'vue-property-decorator';
import Input from '@/components/Input.vue';
import { namespace } from 'vuex-class';
import { BottomWrapper } from '../../BottomWrapper.vue';
import { PayChannelSuccessRate, TransStatement } from '@/common/api/model';
import { TransactionType } from '@/common/config';
import { formatMoney } from '@/common/utils';
import { TransactionInputEvent } from '@/common/types';
import FaCheckSquare from '@/components/icons/FaCheckSquare.vue';
import FaRegSquare from '@/components/icons/FaRegSquare.vue';
import TransactionItemWarning from './TransactionItemWarning.vue';
import UpdatedStatusWarning from './UpdatedStatusWarning.vue';
import { TRANSACTION_PREPOSITION_ENUM, TRANSACTION_STATUS_ENUM, TRANSACTION_STATUS_NAME_ENUM, TransactionStatusName, UpdatedStatusWarningVariant, checkTransactionWarning } from '@/common/transaction-utils';
import { ExternalTransactionKind, ExternalTransactionType } from '@/common/bot/enum/ExternalTransactionType';
import FaAngleRight from '@/components/icons/FaAngleRight.vue';
import { Bridge } from '@/common/bridge';
import { PLEASED_ARTICLE_ACTIONS } from '@/common/bot/flow-frameworks/ChatMessageActions.static';

const userModule = namespace('user');
const chatModule = namespace('chat');
const transactionModule = namespace('transaction');

@Component({
  components: {
    Input,
    BottomWrapper,
    FaCheckSquare,
    FaRegSquare,
    FaAngleRight,
    TransactionItemWarning,
    UpdatedStatusWarning,
  },
  filters: {
    formatDate (value: any) {
      const options = { hour12: false, year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: 'numeric', second: 'numeric' } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatDay (value: any) {
      const options = { hour12: false, year: 'numeric', month: 'numeric', day: 'numeric' } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatTime (value: any) {
      const options = { hour12: false, hour: 'numeric', minute: 'numeric', second: 'numeric' } as const;
      const date = new Date(value);
      return date.toLocaleString(undefined, options);
    },
    formatMoney (value: number) {
      return formatMoney(value);
    },
  },
})
export default class Transaction extends Vue {
  @Prop({ default: () => ([]) }) readonly transactions!: TransStatement[];
  @Prop({ default: null }) readonly transactionsSavedData!: TransactionInputEvent | null;
  @Prop({ default: '' }) readonly transactionType!: TransactionType
  @Prop({ default: null }) readonly externalTransactionKind!: ExternalTransactionKind | null;
  @Prop() readonly transactionMethodSuccessRate!: PayChannelSuccessRate[] | null;

  @userModule.Getter('currency') currency!: string;
  @chatModule.Mutation('updateExtraTransactionInfo') updateExtraTransactionInfo!: Function;

  @transactionModule.State('selectedTransactions') selectedTransactions!: TransStatement[];

  @transactionModule.Getter('transactionStatusFlags') transactionStatusFlags!: {
    isCheckingStatusForTransaction: boolean;
    showTransactionUpdatedWarning: boolean;
    shouldCheckStatusForDeposit: boolean;
  };
  @transactionModule.Getter('updatedTransactionsLength') updatedTransactionsLength!: number;
  @transactionModule.Getter('allSelectedTransactionsUpdated') allSelectedTransactionsUpdated!: boolean;
  @transactionModule.Getter('hasOnlyPendingUpdatedTransactions') hasOnlyPendingUpdatedTransactions!: boolean;
  @transactionModule.Getter('hasOnlySuccessfulUpdatedTransactions') hasOnlySuccessfulUpdatedTransactions!: boolean;
  @transactionModule.Getter('getUpdatedTransactionStatus') getUpdatedTransactionStatus!:(tx: TransStatement) => TransactionStatusName | null;
  @transactionModule.Getter('updatedTransactionsNotification') updatedTransactionsNotification!: { type: UpdatedStatusWarningVariant, text: string };

  @transactionModule.Action('checkTransactionStatus') checkTransactionStatus!: () => Promise<void>;
  @transactionModule.Mutation('addSelectedTransactions') addSelectedTransactions!: (transactions: TransStatement[]) => void;
  @transactionModule.Mutation('resetSelectedTransactions') resetSelectedTransactions!: () => void;
  @transactionModule.Mutation('setShowTransactionUpdatedWarning') setShowTransactionUpdatedWarning!: (show: boolean) => void;

  readonly TRANSACTION_PREPOSITION_ENUM = TRANSACTION_PREPOSITION_ENUM;
  readonly TRANSACTION_STATUS_ENUM = TRANSACTION_STATUS_ENUM;
  readonly TRANSACTION_STATUS_NAME_ENUM = TRANSACTION_STATUS_NAME_ENUM;

  expandedTrans: string[] = [];
  noneSelected: boolean = false;
  localTransactions: TransStatement[] = [];

  get title () {
    const transactionTypeTitle = this.externalTransactionKind
      ? this.$t(
        `page.issue.transaction.title.${this.externalTransactionKind.toLowerCase()}`
      )
      : this.$t('page.issue.transaction.title.transaction');

    return `${this.$t('page.issue.transaction.title.select')} ${transactionTypeTitle}`;
  }

  get selectedTransactionWithWarning () {
    return (this.selectedTransactions || []).some((transaction) => checkTransactionWarning({
      transaction,
      transactionMethodSuccessRate: this.transactionMethodSuccessRate,
      type: this.transactionType,
    }).show);
  }

  get checkDepositStatusLoaderText () {
    return this.$tc('page.issue.transaction.checking_deposit_status', this.selectedTransactions.length);
  }

  get isPendingDepositTransactions () {
    return this.externalTransactionKind === ExternalTransactionType.DEPOSITS_PENDING;
  }

  get buttons () {
    const proceedButton = {
      name: this.selectedTransactionWithWarning ? this.$t('page.issue.transaction.proceed_anyway') : this.$t('page.guest.button.proceed'),
      id: 'proceed' as const,
      className: this.selectedTransactionWithWarning ? 'secondary' : '',
      disabled: !this.canProceed,
    };

    const checkStatusButton = {
      name: this.$tc('page.issue.transaction.check_deposit_status', this.selectedTransactions.length),
      id: 'checkStatus' as const,
    };

    const cancelButton = { name: this.$t('page.issue.transaction.cancel_request'), id: 'cancel' as const };
    const transactionsButton = { name: this.$t('page.issue.transaction.transactions'), id: 'transactions' as const, appendIcon: FaAngleRight };
    const contactSupportButton = { name: this.$t('page.issue.transaction.contact_support'), id: 'proceed' as const, className: !this.hasOnlyPendingUpdatedTransactions ? 'secondary' : '' };

    if (this.hasOnlySuccessfulUpdatedTransactions) {
      return [transactionsButton];
    }

    if (this.isPendingDepositTransactions && this.transactionStatusFlags.shouldCheckStatusForDeposit) {
      return [checkStatusButton];
    }

    if (this.hasOnlyPendingUpdatedTransactions) {
      return [contactSupportButton];
    }

    if (this.allSelectedTransactionsUpdated) {
      return [contactSupportButton, transactionsButton];
    }

    return [
      proceedButton,
      ...(this.selectedTransactionWithWarning ? [cancelButton] : []),
    ];
  }

  get actionType () {
    return TransactionType[this.transactionType];
  }
  get preposition () {
    return this.TRANSACTION_PREPOSITION_ENUM[this.transactionType];
  }
  get isNoRecord () {
    return this.transactions.length === 0;
  }
  get canProceed () {
    return this.selectedTransactions.length || this.noneSelected || this.isNoRecord;
  }
  get operator () {
    return this.transactionType === TransactionType.Deposit ? '+' : '-';
  }
  get isDepositList () {
    return this.transactionType === TransactionType.Deposit;
  }

  get transactionsToShow () {
    return this.localTransactions.length ? this.localTransactions : this.transactions;
  }

  created () {
    if (this.transactionsSavedData) {
      this.resetSelectedTransactions();
      this.addSelectedTransactions(this.transactionsSavedData.selectedTransIssue);
    }

    const key = `local_tx_${this.transactionType === TransactionType.Deposit ? 'deposits' : 'withdrawals'}`;
    const localTransactions = JSON.parse(localStorage.getItem(key) || '[]');
    this.localTransactions = localTransactions;
  }

  selectTransaction (transaction: TransStatement) {
    this.noneSelected = false;
    this.addSelectedTransactions([transaction]);
  }

  expandTrans (tradeId: string) {
    const index = this.expandedTrans.indexOf(tradeId);
    const exist = index !== -1;
    if (exist) {
      this.expandedTrans.splice(index, 1);
    } else {
      this.expandedTrans.push(tradeId);
    }
  }

  selectNone () {
    if (this.noneSelected) {
      this.noneSelected = false;
    } else {
      this.resetSelectedTransactions();
      this.noneSelected = true;
    }
  }

  isTransactionSelected (transaction: TransStatement) {
    return this.selectedTransactions.some((item) => item.tradeId === transaction.tradeId);
  }

  goToTransactions () {
    Bridge.goToAction(PLEASED_ARTICLE_ACTIONS.GO_TRANSACTIONS);
  }

  proceed (selectedButton: { id: string }) {
    if (selectedButton.id === 'cancel') {
      this.$emit('support-request:cancel');
      return;
    }

    if (selectedButton.id === 'checkStatus') {
      this.checkTransactionStatus();
      return;
    }

    if (selectedButton.id === 'transactions') {
      this.goToTransactions();
      return;
    }

    const issueMsg = this.selectedTransactions.reduce((acc: string[], cur) => {
      const authority = cur.counterAuthority ? cur.counterAuthority : cur.counterFull;
      const msg = `${this.actionType} ${authority ? this.preposition : ''} ${authority || ''} ${formatMoney(cur.amount)} ${this.currency} (transaction id: ${cur.tradeId})`;
      return [...acc, msg];
    }, []);

    const data = [{
      extraFieldKey: 'Transaction',
      extraFieldValue: this.selectedTransactions.reduce((acc, cur) => {
        const msg = `${this.actionType} ${this.preposition} ${cur.counterAuthority ? cur.counterAuthority : cur.counterFull} ${formatMoney(cur.amount)} ${this.currency}`;
        return acc ? `${acc} / ${msg}` : `${msg}`;
      }, ''),
    }, {
      extraFieldKey: 'Transaction ID',
      extraFieldValue: this.selectedTransactions.reduce((acc, cur) => acc ? `${acc} / ${cur.tradeId}` : `${cur.tradeId}`, ''),
    }];
    this.updateExtraTransactionInfo(data);

    if (!this.canProceed) return;
    this.emitTransactionInput({ issueMsg, data });
  }

  emitTransactionInput ({ issueMsg, data }: {issueMsg: string[], data: {
    extraFieldKey: string;
    extraFieldValue: string;
  }[]}) {
    const $event: TransactionInputEvent = { type: this.transactionType, issues: issueMsg, none: this.noneSelected, meta: data, empty: this.isNoRecord, selectedTransIssue: this.selectedTransactions };
    this.$emit('transaction:input', $event);
  }
}
